<template>
  <section class="transfer-journey card">
    <LegsContainer
      :legs="outboundLegs"
      :journey="journey"
      direction="outbound"
      :getSelectedSeatsForLeg="getSelectedSeatsForLeg"
      :getSeatChangePrice="getSeatChangePrice"
      :formatDate="formatDate"
      :formatPrice="formatSeatPrice"
      :openSeatSelection="openSeatSelection"
      :isLoading="isLoadingLayout"
      :legLayoutErrors="legLayoutErrors"
      :getLayoutKey="getLayoutKey"
    />
    <LegsContainer
      v-if="hasInboundTrip"
      :legs="inboundLegs"
      :journey="journey"
      direction="inbound"
      :getSelectedSeatsForLeg="getSelectedSeatsForLeg"
      :getSeatChangePrice="getSeatChangePrice"
      :formatDate="formatDate"
      :formatPrice="formatSeatPrice"
      :openSeatSelection="openSeatSelection"
      :isLoading="isLoadingLayout"
      :legLayoutErrors="legLayoutErrors"
      :getLayoutKey="getLayoutKey"
    />
    <CarriageModal
      v-model="isCarriageModalOpen"
      v-if="
        selectedLeg &&
        !isLoadingLayout &&
        !isLoadingPrices &&
        carriageLayouts[getLayoutKey(selectedLeg)]
      "
      class="carriage-modal--desktop"
      :departureStation="selectedLeg?.departure_station.name ?? ''"
      :arrivalStation="selectedLeg?.arrival_station.name ?? ''"
      :serviceName="getServiceNameForLeg"
      :serviceIdentifier="selectedLeg?.service_identifier ?? ''"
      :fromStation="selectedLeg?.departure_station._u_i_c_station_code ?? ''"
      :toStation="selectedLeg?.arrival_station._u_i_c_station_code ?? ''"
      :travelDate="selectedLeg?.service_schedule_date ?? ''"
      :bookingNumber="journey.booking_number"
      :passengerInfo="passengerInfo"
      :selectedSeats="getSelectedSeatsForLeg(selectedLeg, selectedDirection)"
      :currentSegment="getCurrentDirection"
      :legIndex="currentLegIndex"
      :seatPrices="getPricesForLeg(selectedLeg, selectedDirection)"
      :carriageLayout="
        selectedLeg ? carriageLayouts[getLayoutKey(selectedLeg)] : null
      "
      @close="closeModal"
      @save="confirmSeatSelection"
      @seatSelected="handleSeatSelected"
    />
    <CarriageDrawer
      v-model="isModalOpen"
      v-if="
        selectedLeg &&
        !isLoadingLayout &&
        !isLoadingPrices &&
        carriageLayouts[getLayoutKey(selectedLeg)]
      "
      class="carriage-drawer--mobile"
      :departureStation="selectedLeg?.departure_station.name ?? ''"
      :arrivalStation="selectedLeg?.arrival_station.name ?? ''"
      :serviceName="getServiceNameForLeg"
      :serviceIdentifier="selectedLeg?.service_identifier ?? ''"
      :fromStation="selectedLeg?.departure_station._u_i_c_station_code ?? ''"
      :toStation="selectedLeg?.arrival_station._u_i_c_station_code ?? ''"
      :travelDate="selectedLeg?.service_schedule_date ?? ''"
      :bookingNumber="journey.booking_number"
      :passengerInfo="passengerInfo"
      :selectedSeats="getSelectedSeatsForLeg(selectedLeg, selectedDirection)"
      :currentSegment="getCurrentDirection"
      :legIndex="currentLegIndex"
      :seatPrices="getPricesForLeg(selectedLeg, selectedDirection)"
      :carriageLayout="
        selectedLeg ? carriageLayouts[getLayoutKey(selectedLeg)] : null
      "
      @close="closeModal"
      @save="confirmSeatSelection"
      @seatSelected="handleSeatSelected"
    />
  </section>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import type {
    Journey,
    Leg,
  } from '@/types/journeyTypes'
  import type { BookingTariffSegment, BookingJourneySegment, Segment } from '@/types/bookingTypes'
  import type { Station } from '@/types/stationTypes'
  import type { CarriageSeat, SelectedSeat } from '@/types/seatTypes'
  import { CarriageLayout } from '@/types/bookingTypes'
  import CarriageModal from './CarriageModal.vue'
  import CarriageDrawer from './CarriageDrawer.vue'
  import LegsContainer from './LegsContainer.vue'
  import { format } from 'date-fns'
  import { nb } from 'date-fns/locale'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { formatPrice } from '../../../utils/formatters'
  import { PropType } from 'vue'
  import {
    fetchCarriageLayout,
    searchProducts,
  } from '@/services/journeyService'

  export function formatTime(timestamp: string): string {
    const date = new Date(timestamp)
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', timestamp)
      return 'Invalid date'
    }
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}.${minutes}`
  }

  export default defineComponent({
    name: 'TransferJourney',
    components: {
      CarriageModal,
      CarriageDrawer,
      LegsContainer,
    },
    props: {
      journey: {
        type: Object as PropType<Journey>,
        required: true,
      },
      hasInboundTrip: {
        type: Boolean,
        default: false,
      },
      passengerInfo: {
        type: Array as PropType<any[]>,
        required: true,
      },
      seatChangePrices: {
        type: Object as PropType<{ [key: string]: number }>,
        required: false,
        default: () => ({}),
      },
    },
    setup(props, { emit }) {
      const errorStore = useErrorStore()
      const seatStore = useSeatReservationStore()
      const router = useRouter()
      const route = useRoute()
      const myTravelStore = useMyTravelStore()
      const isModalOpen = ref(false)
      const isCarriageModalOpen = ref(false)
      const selectedLeg = ref<Leg | null>(null)
      const selectedDirection = ref<'outbound' | 'inbound'>('outbound')
      const carriageLayouts = ref<{ [key: string]: CarriageLayout }>({})
      const isLoadingLayout = ref(false)
      const seatPrices = ref<{ [key: string]: { [key: string]: any } }>({})
      const isLoadingPrices = ref(false)
      const legLayoutErrors = ref<{ [key: string]: boolean }>({})
      const isLoading = ref(false)

      const getLayoutKey = (
        segmentOrLeg: any
      ): string => {
        if (!segmentOrLeg) return ''

        if ('service_identifier' in segmentOrLeg && 
            'departure_station' in segmentOrLeg && 
            'arrival_station' in segmentOrLeg && 
            !('id' in segmentOrLeg)) {
          const departureCode = segmentOrLeg.departure_station?._u_i_c_station_code || ''
          const arrivalCode = segmentOrLeg.arrival_station?._u_i_c_station_code || ''
          return `${segmentOrLeg.service_identifier || ''}_${departureCode}_${arrivalCode}`
        }
        
        if ('id' in segmentOrLeg && 'service_identifier' in segmentOrLeg) {
          return `${segmentOrLeg.service_identifier || ''}_${segmentOrLeg.id || ''}`
        }
        
        return ''
      }

      const getLegForSegment = (
        segment: Segment | BookingTariffSegment | null
      ): Leg | null => {
        if (!segment || !segment.departure_station || !segment.arrival_station)
          return null

        const departureStation =
          typeof segment.departure_station === 'string'
            ? ({
                name: '',
                _u_i_c_station_code: segment.departure_station,
                short_code: '',
                country_code: '',
              } as Station)
            : segment.departure_station

        const arrivalStation =
          typeof segment.arrival_station === 'string'
            ? ({
                name: '',
                _u_i_c_station_code: segment.arrival_station,
                short_code: '',
                country_code: '',
              } as Station)
            : segment.arrival_station

        return {
          departure_station: departureStation,
          arrival_station: arrivalStation,
          departure_timestamp: '',
          arrival_timestamp: '',
          service_name: '',
          service_identifier: segment.service_identifier || '',
          service_schedule_date: '',
        }
      }

      const isSeatReservationAvailable = (segment: any) => {
        return (
          segment?.required_products?.some(
            (product: any) => 'seat' in product
          ) || false
        )
      }

      const isSegmentCancelled = (segment: any) => {
        if (!segment) {
          return false
        }
        
        // Check the segment's own cancelled property, not individual products
        return segment.cancelled === true
      }

      const checkSeatAvailability = () => {
        props.journey.outbound_booking_tariff_segments.forEach(
          (segment: any) => {
            const layoutKey = getLayoutKey(segment)
            legLayoutErrors.value[layoutKey] =
              !isSeatReservationAvailable(segment)
          }
        )

        if (
          props.hasInboundTrip &&
          props.journey.inbound_booking_tariff_segments?.length
        ) {
          props.journey.inbound_booking_tariff_segments.forEach(
            (segment: any) => {
              const layoutKey = getLayoutKey(segment)
              legLayoutErrors.value[layoutKey] =
                !isSeatReservationAvailable(segment)
            }
          )
        }
      }

      onMounted(async () => {
        try {
          if (!props.journey) return
          checkSeatAvailability()
          const outboundSegments =
            props.journey.outbound_booking_tariff_segments || []

          for (let i = 0; i < outboundSegments.length; i++) {
            const segment = outboundSegments[i]
            if (!segment) continue

            if (!isSeatReservationAvailable(segment)) {
              continue
            }

            const journeySegment = props.journey.outbound_segments?.[i]
            
            const getServiceName = () => {
              if (journeySegment && 'service_name' in journeySegment) {
                return journeySegment.service_name
              }
              if (segment && 'validity_service' in segment) {
                return segment.validity_service
              }
              return '' 
            }
            
            const serviceName = getServiceName()
            if (!journeySegment || !serviceName) {
              console.error('Missing journey segment or service name for segment:', segment)
              continue
            }

            const layout = await fetchCarriageLayout(
              serviceName,
              journeySegment.departure_station?._u_i_c_station_code || '',
              journeySegment.arrival_station?._u_i_c_station_code || '',
              segment.travel_date
            )

            if (!layout) {
              console.error('No layout data received for segment:', segment)
              continue
            }
            if (layout?.carriages) {
              layout.carriages.forEach((carriage: any) => {
                if (carriage.seats) {
                  carriage.seats = carriage.seats.map((seat: any) => {
                    let status = 'AVAILABLE'
                    if (seat.allocated) {
                      status = 'ALLOCATED'
                    } else if (seat.blocked) {
                      status = 'BLOCKED'
                    }
                    return { ...seat, status }
                  })
                }
              })
            }

            if (layout.seat_properties) {
              seatStore.setSeatProperties(layout.seat_properties)
            }

            carriageLayouts.value[getLayoutKey(segment)] = layout
          }

          const inboundSegments =
            props.journey.inbound_booking_tariff_segments || []

          for (let i = 0; i < inboundSegments.length; i++) {
            const segment = inboundSegments[i]
            if (!segment) continue

            if (!isSeatReservationAvailable(segment)) {
              continue
            }

            const leg = props.journey.inbound_segments?.[i] as Segment
            
            const serviceName = leg?.service_name || segment.validity_service
            if (!leg || !serviceName) {
              console.error('Missing leg or service name for segment:', segment)
              continue
            }

            const layout = await fetchCarriageLayout(
              serviceName,
              leg.departure_station._u_i_c_station_code,
              leg.arrival_station._u_i_c_station_code,
              segment.travel_date
            )

            if (!layout) {
              console.error('No layout data received for segment:', segment)
              continue
            }

            if (layout?.carriages) {
              layout.carriages.forEach((carriage: any) => {
                if (carriage.seats) {
                  carriage.seats = carriage.seats.map((seat: any) => {
                    let status = 'AVAILABLE'
                    if (seat.allocated) {
                      status = 'ALLOCATED'
                    } else if (seat.blocked) {
                      status = 'BLOCKED'
                    }
                    return { ...seat, status }
                  })
                }
              })
            }

            if (layout.seat_properties) {
              seatStore.setSeatProperties(layout.seat_properties)
            }

            carriageLayouts.value[getLayoutKey(segment)] = layout
          }
        } catch (error) {
          console.error('Error loading layouts:', error)
        }
      })

      const getServiceNameForLeg = computed(() => {
        if (!selectedLeg.value) return ''

        const outboundSegment =
          props.journey?.outbound_booking_tariff_segments?.find(
            (segment) =>
              segment.service_identifier ===
              selectedLeg.value?.service_identifier
          )
        if (outboundSegment?.booking_journey_segments?.[0]?.service_name) {
          return outboundSegment.booking_journey_segments[0].service_name
        }

        const inboundSegment =
          props.journey?.inbound_booking_tariff_segments?.find(
            (segment) =>
              segment.service_identifier ===
              selectedLeg.value?.service_identifier
          )
        if (inboundSegment?.booking_journey_segments?.[0]?.service_name) {
          return inboundSegment.booking_journey_segments[0].service_name
        }

        return selectedLeg.value.service_name || ''
      })

      const outboundLegs = computed(() => {
        if (!props.journey?.outbound_booking_tariff_segments) return []

        const activeSegments =
          props.journey.outbound_booking_tariff_segments.filter((segment) => 
            !segment.required_products?.every((product) => product.cancelled)
          )

        return activeSegments.map((tariffSegment, index, array) => {
          const journeySegment = tariffSegment.booking_journey_segments?.[0]
          
          return {
            ...tariffSegment,
            departure_station: tariffSegment.departure_station,
            arrival_station: tariffSegment.arrival_station,
            service_identifier: tariffSegment.service_identifier,
            service_schedule_date: tariffSegment.travel_date,
            departure_timestamp: journeySegment?.departure_date_time || '',
            arrival_timestamp: journeySegment?.arrival_date_time || '',
            service_name: journeySegment?.service_name || tariffSegment.service_type?.name || '',
            direction: 'outbound',
          }
        })
      })

      const inboundLegs = computed(() => {
        if (!props.journey?.inbound_booking_tariff_segments) return []

        const activeSegments =
          props.journey.inbound_booking_tariff_segments.filter((segment) => 
            !segment.required_products?.every((product) => product.cancelled)
          )

        return activeSegments.map((tariffSegment, index, array) => {
          const journeySegment = tariffSegment.booking_journey_segments?.[0]
          
          return {
            ...tariffSegment,
            departure_station: tariffSegment.departure_station,
            arrival_station: tariffSegment.arrival_station,
            service_identifier: tariffSegment.service_identifier,
            service_schedule_date: tariffSegment.travel_date,
            departure_timestamp: journeySegment?.departure_date_time || '',
            arrival_timestamp: journeySegment?.arrival_date_time || '',
            service_name: journeySegment?.service_name || tariffSegment.service_type?.name || '',
            direction: 'inbound',
          }
        })
      })

      const currentLegIndex = computed(() => {
        if (!selectedLeg.value || !selectedDirection.value) return 0
        
        const journeyLegs =
          selectedDirection.value === 'outbound' 
            ? outboundLegs.value 
            : inboundLegs.value
            
        if (!journeyLegs) return 0
        
        const legIndex = journeyLegs.findIndex(
          (l) =>
            l.service_identifier === selectedLeg.value?.service_identifier &&
            l.departure_station._u_i_c_station_code ===
              selectedLeg.value?.departure_station._u_i_c_station_code
        )
        
        return legIndex >= 0 ? legIndex : 0
      })

      const getCurrentSegment = computed(() => {
        if (!selectedLeg.value || !selectedDirection.value) return null
        const segment = {
          direction: selectedDirection.value,
          serviceIdentifier: selectedLeg.value.service_identifier,
          fromStation: selectedLeg.value.departure_station._u_i_c_station_code,
          toStation: selectedLeg.value.arrival_station._u_i_c_station_code,
          travelDate: selectedLeg.value.departure_timestamp,
          serviceName: selectedLeg.value.service_name,
          segmentId: selectedLeg.value.id,
        }
        return segment
      })

      const getCurrentDirection = computed(() => {
        if (!selectedDirection.value) return 'outbound'
        return selectedDirection.value === 'inbound' ? 'inbound' : 'outbound'
      })

      const getPriceKey = (leg: Leg, direction: 'outbound' | 'inbound') => {
        return `${direction}_${leg.service_identifier}_${leg.departure_station._u_i_c_station_code}_${leg.arrival_station._u_i_c_station_code}_${leg.service_schedule_date}`
      }

      const fetchSeatPrices = async (leg: any, key: string) => {
        try {
          isLoadingPrices.value = true
          const prices = await searchProducts(
            leg.service_identifier,
            leg.direction,
            leg.departure_station._u_i_c_station_code,
            leg.arrival_station._u_i_c_station_code,
            leg.service_schedule_date,
            [{ code: 'SF' }]
          )

          await seatStore.initializeSeatProducts(prices, leg.direction)

          const priceMap: { [key: string]: any } = {}
          prices.data.segment.products.forEach((product: any) => {
            if (product.passenger_fares?.[0]?.price) {
              const amount = product.passenger_fares[0].price.amount
              priceMap[product.product_code] = {
                price: {
                  amount,
                  currencyCode: product.passenger_fares[0].price.currency_code,
                },
                tariffCode: product.passenger_fares[0].tariff_code,
                description: seatStore.getSeatPropertyDescription(
                  product.product_code
                ),
              }
            }
          })

          const layout = carriageLayouts.value[key]
          if (layout?.carriages) {
            layout.carriages.forEach((carriage: any) => {
              if (carriage.seats) {
                carriage.seats = carriage.seats.map((seat: any) => {
                  let status = 'AVAILABLE'
                  if (seat.allocated || seat.status === 'ALLOCATED') {
                    status = 'ALLOCATED'
                  } else if (seat.blocked || seat.status === 'BLOCKED') {
                    status = 'BLOCKED'
                  }
                  const processedSeat = {
                    ...seat,
                    status,
                    seat_number: seat.seat_number || seat.number,
                  }
                  return processedSeat
                })
              }
            })
          }

          // Set seat properties in the store
          if (layout.seat_properties) {
            seatStore.setSeatProperties(layout.seat_properties)
          }

          seatPrices.value[key] = priceMap
          return priceMap
        } catch (error) {
          console.error('Error fetching seat prices:', error)
          return {}
        } finally {
          isLoadingPrices.value = false
        }
      }

      const getPricesForLeg = (leg: any, direction: string) => {
        const key = getLayoutKey(leg)
        return seatPrices.value[key] || {}
      }

      const loadCarriageLayout = async (leg: Leg) => {
        const key = getLayoutKey(leg)
        if (carriageLayouts.value[key]) {
          return carriageLayouts.value[key]
        }

        try {
          isLoadingLayout.value = true

          let formattedDate: string
          if (leg.departure_timestamp) {
            try {
              const date = new Date(leg.departure_timestamp)
              formattedDate = format(date, 'yyyy-MM-dd')
            } catch (error) {
              console.error(
                'Error formatting departure_timestamp:',
                leg.departure_timestamp,
                error
              )

              formattedDate = leg.service_schedule_date || ''
            }
          } else {
            formattedDate = leg.service_schedule_date || ''
          }

          if (!formattedDate) {
            throw new Error('No valid date found in leg data')
          }

          const layout = await fetchCarriageLayout(
            leg.service_name,
            leg.departure_station._u_i_c_station_code,
            leg.arrival_station._u_i_c_station_code,
            formattedDate
          )

          if (!layout) {
            console.error('No layout data received')
            return null
          }

          if (layout?.carriages) {
            layout.carriages.forEach((carriage: any) => {
              if (carriage.seats) {
                carriage.seats = carriage.seats.map((seat: any) => {
                  let status = 'AVAILABLE'
                  if (seat.allocated || seat.status === 'ALLOCATED') {
                    status = 'ALLOCATED'
                  } else if (seat.blocked || seat.status === 'BLOCKED') {
                    status = 'BLOCKED'
                  }
                  const processedSeat = {
                    ...seat,
                    status,
                    seat_number: seat.seat_number || seat.number,
                  }
                  return processedSeat
                })
              }
            })
          }

          if (layout.seat_properties) {
            seatStore.setSeatProperties(layout.seat_properties)
          }

          carriageLayouts.value[key] = layout
          return layout
        } catch (error) {
          console.error('Error loading carriage layout:', error)
          return null
        } finally {
          isLoadingLayout.value = false
        }
      }

      const openSeatSelection = async (
        leg: Leg,
        direction: 'outbound' | 'inbound'
      ) => {
        const segment = props.journey[
          `${direction}_booking_tariff_segments`
        ]?.find((s) => s.service_identifier === leg.service_identifier)

        const hasSeatReservation = seatStore.hasSeatReservation(
          direction,
          segment?.id
        )
        const isSeatAvailable = isSeatReservationAvailable(segment)

        if (!segment || !hasSeatReservation || !isSeatAvailable) {
          return
        }

        try {
          const layout = await loadCarriageLayout(leg)
          if (!layout) {
            console.error('[DEBUG] openSeatSelection - No layout returned')
            errorStore.setError('Error loading carriage layout')
            return
          }
          await fetchSeatPrices(leg, getLayoutKey(leg))

          selectedLeg.value = leg
          selectedDirection.value = direction
          isCarriageModalOpen.value = true
        } catch (error) {
          console.error('[DEBUG] openSeatSelection - Error:', {
            error,
            message: error instanceof Error ? error.message : 'Unknown error',
          })
          errorStore.setError(
            error instanceof Error ? error.message : 'Error in seat selection'
          )
          return
        }
      }

      const formatDate = (date: string) => {
        if (!date) return ''
        try {
          return new Intl.DateTimeFormat('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(new Date(date))
        } catch (error) {
          console.error('Error formatting date:', date, error)
          return date
        }
      }

      const formatSeatPrice = (price: number) => {
        return formatPrice(price)
      }

      const getSelectedSeatsForLeg = (
        leg: Leg,
        direction: 'outbound' | 'inbound'
      ) => {
        const journeyLegs =
          direction === 'outbound' ? outboundLegs.value : inboundLegs.value
        if (!journeyLegs) return []

        const legIndex = journeyLegs.findIndex(
          (l) =>
            l.service_identifier === leg.service_identifier &&
            l.departure_station._u_i_c_station_code ===
              leg.departure_station._u_i_c_station_code
        )

        // Get the seats from the store
        return seatStore.getSelectedSeatsForLeg(direction, legIndex)
      }

      const getSeatChangePrice = (
        index: number,
        direction: 'outbound' | 'inbound'
      ) => {
        const legs =
          direction === 'outbound' ? outboundLegs.value : inboundLegs.value
        if (!legs) return 0

        const leg = legs[index]
        if (!leg) return 0

        const key = `${direction}_${index}`
        const currentSeats = seatStore.selectedSeats[key] || []

        return currentSeats.reduce((total, seat) => {
          return total + (seat.price?.amount || 0)
        }, 0)
      }

      const updateSeatPrices = async () => {
        try {
          const selectedSeats = getSelectedSeatsForLeg(
            selectedLeg.value,
            selectedDirection.value
          )

          const totalPrice = selectedSeats.reduce((total, seat) => {
            return total + (seat.price?.amount || 0)
          }, 0)

          myTravelStore.updateSelectedSeats(
            selectedDirection.value,
            selectedSeats,
            totalPrice
          )
        } catch (error) {
          console.error('Error updating seat prices:', error)
        }
      }

      const closeModal = () => {
        isModalOpen.value = false
        isCarriageModalOpen.value = false
        selectedLeg.value = null
        selectedDirection.value = null
      }

      const handleSeatSelected = (event: {
        seat: CarriageSeat
        legIndex: number
        direction: 'outbound' | 'inbound'
      }) => {
        try {
          const { seat, legIndex, direction } = event
          
          // Get the correct segment data from the filtered legs instead of re-indexing 
          // into the original segments array to avoid cancelled segment mismatches
          const journeyLegs = direction === 'outbound' ? outboundLegs.value : inboundLegs.value
          const selectedLegData = journeyLegs[legIndex]
          
          if (!selectedLegData) {
            console.error('No leg data found for legIndex:', legIndex, 'in direction:', direction)
            return
          }
          
          // Use the leg data directly instead of re-indexing into original segments
          const combinedSegment: BookingTariffSegment & Partial<Leg> = {
            ...selectedLegData,
            service_identifier: selectedLegData.service_identifier,
            departure_station: selectedLegData.departure_station,
            arrival_station: selectedLegData.arrival_station,
          }

          seatStore.handleSeatSelection({
            seat,
            segment: combinedSegment,
            direction,
            legIndex,
          })

          updateSeatPrices()
        } catch (error) {
          console.error('Error in handleSeatSelected:', error)
        }
      }

      // Helper method to get correct tariff code from seat product data
      const getTariffCodeForSeat = (seat: SelectedSeat): string => {
        const leg = seat.direction === 'outbound' 
          ? outboundLegs.value[seat.legIndex]
          : inboundLegs.value[seat.legIndex]
        
        if (!leg) {
          return seat.tariffCode || ''
        }

        const priceKey = getPriceKey(leg, seat.direction)
        const prices = seatPrices.value[priceKey]
        
        if (!prices) {
          console.warn(`[DEBUG] No prices found for leg ${seat.legIndex}, using seat.tariffCode`)
          return seat.tariffCode || ''
        }

        const productCode = seat.property_code || 'STANDARD_SEAT'
        const seatProduct = prices[productCode]
        
        if (seatProduct && seatProduct.tariffCode) {
          return seatProduct.tariffCode
        }

        return seat.tariffCode || ''
      }

      const handleContinue = async () => {
        if (isLoading.value) return

        isLoading.value = true
        try {
          const bookingNumber = props.journey?.booking_number
          if (!bookingNumber) {
            throw new Error('No booking number found')
          }

    // USE FILTERED LEGS - Same as seat selection logic!
    const journeyOutboundLegs = outboundLegs.value || []
    const allOutboundSeats = []
    
    for (let legIndex = 0; legIndex < journeyOutboundLegs.length; legIndex++) {
      const currentSegment = journeyOutboundLegs[legIndex]
      
      // Check if the entire segment is cancelled before processing any seats
      if (currentSegment && isSegmentCancelled(currentSegment)) {
        console.warn('[SEAT_PATCH] Skipping entire cancelled segment:', {
          legIndex,
          segmentId: currentSegment.id,
          serviceIdentifier: currentSegment.service_identifier
        })
        continue // Skip to next segment entirely
      }
      
      const seats = seatStore.getSelectedSeatsForLeg('outbound', legIndex)

      if (seats.length > 0) {
        
        for (const seat of seats) {
          // Validate that the seat belongs to the current segment to prevent duplicate processing
          if (seat.segmentId !== currentSegment.id) {
            console.warn('[SEAT_PATCH] MISMATCH: Seat segmentId does not match current segment - skipping:', {
              legIndex,
              expectedSegmentId: currentSegment.id,
              actualSeatSegmentId: seat.segmentId,
              seatNumber: seat.number,
              currentSegmentService: currentSegment.service_identifier
            })
            continue // Skip this seat as it doesn't belong to current segment
          }
          
          // Use currentSegment instead of finding it again
          const matchingSegment = currentSegment
          
          // Use the existing correct segmentId from seat selection
          if (seat.segmentId && !seat.item_ref) {
            if (matchingSegment && isSeatReservationAvailable(matchingSegment)) {
              const product = matchingSegment?.required_products?.[0]
              if (product?.item_ref) {
                seat.item_ref = product.item_ref
              }
            }
          }
          
          allOutboundSeats.push(seat)
        }
      }
    }

    if (allOutboundSeats.length > 0) {
      const deduplicatedOutboundSeats = []
      const seenKeys = new Set()
      
      for (const seat of allOutboundSeats) {
        const uniqueKey = `${seat.passenger_id}_${seat.segmentId}`
        if (!seenKeys.has(uniqueKey)) {
          seenKeys.add(uniqueKey)
          deduplicatedOutboundSeats.push(seat)
        }
      }

      for (const seat of deduplicatedOutboundSeats) {
        const correctTariffCode = getTariffCodeForSeat(seat)
        
        const newSeat = {
          carriage: seat.carriage,
          number: seat.seat_number
        }
        
        await seatStore.patchBookingWithProduct(
          bookingNumber,
          correctTariffCode,
          'outbound',
          seat.passenger_id,
          seat.item_ref, // Pass the pre-assigned item_ref from this component
          newSeat,
          true, 
          seat.segmentId 
        )
      }
    }

    // Process inbound seats
    const inboundSegments = props.journey?.inbound_booking_tariff_segments || []
    const allInboundSeats = []

    for (let legIndex = 0; legIndex < inboundSegments.length; legIndex++) {
      const currentSegment = inboundSegments[legIndex]
      
      // Check if the entire segment is cancelled before processing any seats
      if (currentSegment && isSegmentCancelled(currentSegment)) {
        console.warn('[SEAT_PATCH] Skipping entire cancelled inbound segment:', {
          legIndex,
          segmentId: currentSegment.id,
          serviceIdentifier: currentSegment.service_identifier
        })
        continue // Skip to next segment entirely
      }
      
      const seats = seatStore.getSelectedSeatsForLeg('inbound', legIndex)

      if (seats.length > 0) {
        for (const seat of seats) {
          // Validate that the seat belongs to the current segment to prevent duplicate processing
          if (seat.segmentId !== currentSegment.id) {
            console.warn('[SEAT_PATCH] Inbound seat segmentId mismatch - skipping to prevent duplicate processing:', {
              legIndex,
              expectedSegmentId: currentSegment.id,
              actualSeatSegmentId: seat.segmentId,
              seatNumber: seat.number
            })
            continue // Skip this seat as it doesn't belong to current segment
          }
          
          // Use currentSegment instead of finding it again
          const matchingSegment = currentSegment
          
          // Use the existing correct segmentId from seat selection
          if (seat.segmentId && !seat.item_ref) {
            if (matchingSegment && isSeatReservationAvailable(matchingSegment)) {
              const product = matchingSegment?.required_products?.[0]
              if (product?.item_ref) {
                seat.item_ref = product.item_ref
              }
            }
          }
          
          allInboundSeats.push(seat)
        }
      }
    }

    if (allInboundSeats.length > 0) {
      const deduplicatedInboundSeats = []
      const seenKeys = new Set()
      
      for (const seat of allInboundSeats) {
        const uniqueKey = `${seat.passenger_id}_${seat.segmentId}`
        if (!seenKeys.has(uniqueKey)) {
          seenKeys.add(uniqueKey)
          deduplicatedInboundSeats.push(seat)
        }
      }

      for (const seat of deduplicatedInboundSeats) {
        const correctTariffCode = getTariffCodeForSeat(seat)
        
        const newSeat = {
          carriage: seat.carriage,
          number: seat.seat_number
        }
        
        await seatStore.patchBookingWithProduct(
          bookingNumber,
          correctTariffCode,
          'inbound',
          seat.passenger_id,
          seat.item_ref, // Pass the pre-assigned item_ref from this component
          newSeat,
          true, 
          seat.segmentId 
        )
      }
    }

          // Preserve rebooking query parameters when navigating to EquipmentPage
          if (route.query.isRebooking) {
            await router.push({
              name: 'EquipmentPage',
              query: {
                isRebooking: route.query.isRebooking,
                rebookingType: route.query.rebookingType,
                bookingId: route.query.bookingId,
                itemsToReplace: route.query.itemsToReplace
              }
            })
          } else {
            await router.push('/reiseplanlegger/tillegg')
          }
        } catch (error) {
          console.error('Error handling continue:', error)
        } finally {
          isLoading.value = false
        }
      }

      const confirmSeatSelection = () => {
        closeModal()
      }

      return {
        isModalOpen,
        isCarriageModalOpen,
        selectedLeg,
        selectedDirection,
        carriageLayouts,
        isLoadingLayout,
        seatPrices,
        isLoadingPrices,
        legLayoutErrors,
        outboundLegs,
        inboundLegs,
        currentLegIndex,
        getLayoutKey,
        getSelectedSeatsForLeg,
        getSeatChangePrice,
        formatDate,
        formatSeatPrice,
        openSeatSelection,
        closeModal,
        confirmSeatSelection,
        handleSeatSelected,
        getServiceNameForLeg,
        getPricesForLeg,
        getCurrentDirection,
        updateSeatPrices,
        handleContinue,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .transfer-journey {
    padding: rem(20) 20px;
    text-align: left;
    color: $clr-text;
  }

  .carriage-modal--desktop {
    @media (max-width: 767px) {
      display: none;
    }
  }

  .carriage-drawer--mobile {
    @media (min-width: 768px) {
      display: none;
    }
  }
</style>
